import React, { useState, useEffect } from 'react';
import { getAdminCertificateOrders } from '../../services/api';
import { Award, Search, Phone, Mail, MapPin, Calendar, CheckCircle, Package } from 'lucide-react';

const CertificateOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await getAdminCertificateOrders();
      if (res.success) {
        setOrders(res.data || []);
      }
    } catch (e) {
      console.error("Error fetching certificate orders:", e);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(o =>
    (o.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (o.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (o.phone || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (o.order_reference || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (o.course_title || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-emerald-800 to-teal-900 rounded-2xl p-6 text-white shadow-md flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Award className="text-amber-400" size={24} />
            <h1 className="text-xl sm:text-2xl font-black">Hardcopy Certificate Requests</h1>
          </div>
          <p className="text-xs sm:text-sm text-emerald-100">
            View & manage student NCVET & ASDC hardcopy certificate delivery orders.
          </p>
        </div>
        <div className="bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 rounded-xl text-center shrink-0">
          <p className="text-[10px] text-emerald-200 uppercase font-bold">Total Orders</p>
          <p className="text-xl font-black text-white">{orders.length}</p>
        </div>
      </div>

      {/* Filter / Search Bar */}
      <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-3">
        <Search className="text-gray-400" size={18} />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by student name, email, phone, course title or order ref..."
          className="w-full text-xs sm:text-sm focus:outline-none text-gray-800"
        />
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500 text-sm">Loading certificate orders...</div>
        ) : filteredOrders.length === 0 ? (
          <div className="p-12 text-center text-gray-400 text-sm">No certificate orders found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs sm:text-sm">
              <thead className="bg-gray-50 border-b border-gray-100 text-gray-500 uppercase text-[10px] font-bold tracking-wider">
                <tr>
                  <th className="py-3.5 px-4">Order Ref & Date</th>
                  <th className="py-3.5 px-4">Candidate Info</th>
                  <th className="py-3.5 px-4">Course Title</th>
                  <th className="py-3.5 px-4">Delivery Address</th>
                  <th className="py-3.5 px-4">Amount</th>
                  <th className="py-3.5 px-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50/80 transition-colors">
                    <td className="py-3.5 px-4">
                      <p className="font-mono font-bold text-emerald-700 text-xs">{order.order_reference}</p>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        {order.created_at ? new Date(order.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                      </p>
                    </td>

                    <td className="py-3.5 px-4 space-y-0.5">
                      <p className="font-bold text-gray-900">{order.full_name}</p>
                      {order.father_name && <p className="text-[11px] text-gray-500">S/o: {order.father_name}</p>}
                      <p className="text-[11px] text-gray-500 flex items-center gap-1">
                        <Mail size={12} className="text-gray-400" /> {order.email}
                      </p>
                      <p className="text-[11px] text-gray-500 flex items-center gap-1">
                        <Phone size={12} className="text-gray-400" /> {order.phone}
                      </p>
                    </td>

                    <td className="py-3.5 px-4">
                      <p className="font-semibold text-gray-800 max-w-xs">{order.course_title}</p>
                      <span className="inline-block mt-1 bg-amber-50 text-amber-800 border border-amber-200 text-[10px] font-bold px-2 py-0.5 rounded-full">
                        NCVET Hardcopy Certificate
                      </span>
                    </td>

                    <td className="py-3.5 px-4 max-w-xs">
                      <div className="flex items-start gap-1 text-[11px] text-gray-600">
                        <MapPin size={14} className="text-emerald-600 shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-900">{order.address}</p>
                          <p>{order.city}, {order.state} - <strong className="text-gray-900">{order.pincode}</strong></p>
                        </div>
                      </div>
                    </td>

                    <td className="py-3.5 px-4 font-black text-gray-900 text-sm">
                      ₹{order.amount}
                    </td>

                    <td className="py-3.5 px-4">
                      <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 text-[11px] font-bold px-2.5 py-1 rounded-full">
                        <CheckCircle size={12} /> {order.payment_status || 'Paid'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CertificateOrders;
